import type {ViewInspection} from './index';
import type {CommittedViewInspection} from './inspectionRegistry';
import type {
  InspectionDescriptor,
  InspectionEvent,
  InspectionTarget,
  InspectionSnapshot,
  ViewInspectionService,
} from './types';
import {serializeInspection} from './serialize';

const MAX_CACHED_SNAPSHOTS = 20;
const MAX_DISCOVERED_VIEWS = 64;
const MAX_LABEL_LENGTH = 256;

/** The existing registry owns identity and lifecycle; the service only publishes it. */
export interface InspectionInstance {
  readonly instanceId: number;
  readonly revision: number;
  readonly element?: Element;
  readonly committed?: {
    readonly view: CommittedViewInspection;
    readonly parent?: {readonly instanceId: number};
  };
}

interface PublishedInstance {
  readonly inspection: ViewInspection;
  readonly revision: number;
  readonly parentId?: number;
  readonly element?: Element;
}

interface Subscription {
  readonly instanceId: number;
  receive?: (event: InspectionEvent) => void;
}

interface PendingDelivery {
  readonly subscription: Subscription;
  readonly sessionId: string;
  readonly type: 'updated' | 'removed';
}

function deliver(
  receive: Subscription['receive'],
  event: InspectionEvent,
): void {
  try {
    receive?.(Object.freeze(event));
  } catch {
    // A broken extension receiver must not affect rendering or peers.
  }
}

function createSessionId(): string {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

function createService(
  readAttachedIds: (element: Element) => readonly number[],
) {
  const sessionPrefix = createSessionId();
  let sessionGeneration = 0;
  let sessionId = `${sessionPrefix}:${sessionGeneration}`;
  const published = new Map<number, PublishedInstance>();
  const snapshots = new Map<number, InspectionSnapshot>();
  const attached = new WeakMap<Element, readonly number[]>();
  const pending = new Set<InspectionInstance>();
  const subscriptions = new Set<Subscription>();

  function getSnapshot(instanceId: number): InspectionSnapshot | undefined {
    const entry = published.get(instanceId);
    if (!entry) {
      return undefined;
    }
    const snapshot =
      snapshots.get(instanceId) ??
      serializeInspection(
        entry.inspection,
        sessionId,
        instanceId,
        entry.revision,
      );
    // Refresh insertion order so the least recently read snapshot is evicted.
    snapshots.delete(instanceId);
    snapshots.set(instanceId, snapshot);
    if (snapshots.size > MAX_CACHED_SNAPSHOTS) {
      snapshots.delete(snapshots.keys().next().value!);
    }
    return snapshot;
  }

  function getViewsForElement(element: Element): {
    views: readonly InspectionDescriptor[];
    truncated: boolean;
  } {
    const results = new Map<number, InspectionDescriptor>();
    let truncated = false;
    const label = (value: string) => {
      if (value.length <= MAX_LABEL_LENGTH) {
        return value;
      }
      truncated = true;
      return `${value.slice(0, MAX_LABEL_LENGTH - 1)}…`;
    };
    for (let node: Element | null = element; node; node = node.parentElement) {
      for (const id of attached.get(node) ?? []) {
        let current: number | undefined = id;
        while (current !== undefined && !results.has(current)) {
          const entry = published.get(current);
          if (!entry) {
            break;
          }
          if (results.size === MAX_DISCOVERED_VIEWS) {
            return {
              views: Object.freeze(Array.from(results.values())),
              truncated: true,
            };
          }
          results.set(
            current,
            Object.freeze({
              instanceId: current,
              revision: entry.revision,
              view: label(entry.inspection.owner.view),
              config: label(entry.inspection.owner.config),
            }),
          );
          current = entry.parentId;
        }
      }
    }
    return {views: Object.freeze(Array.from(results.values())), truncated};
  }

  function unsubscribe(subscription: Subscription): void {
    if (!subscriptions.delete(subscription)) {
      return;
    }
    subscription.receive = undefined;
    if (!subscriptions.size) {
      snapshots.clear();
    }
  }

  function publishPendingInspections(): void {
    const changes = new Map<number, 'updated' | 'removed'>();
    const elements = new Set<Element>();
    for (const instance of pending) {
      const {instanceId, committed, element, revision} = instance;
      const previous = published.get(instanceId);
      if (previous?.element) {
        elements.add(previous.element);
      }
      if (element) {
        elements.add(element);
      }
      if (committed) {
        const unchanged =
          previous?.inspection === committed.view.inspection &&
          previous.revision === revision;
        published.set(instanceId, {
          inspection: committed.view.inspection,
          revision,
          parentId: committed.parent?.instanceId,
          element,
        });
        if (!unchanged) {
          snapshots.delete(instanceId);
          changes.set(instanceId, 'updated');
        }
      } else if (published.delete(instanceId)) {
        snapshots.delete(instanceId);
        changes.set(instanceId, 'removed');
      }
    }
    pending.clear();
    for (const element of elements) {
      // Match the registry's innermost-first ref attachment order.
      const ids = readAttachedIds(element)
        .filter((id) => published.has(id))
        .reverse();
      if (ids.length) {
        attached.set(element, ids);
      } else {
        attached.delete(element);
      }
    }
    // Publish the entire batch before calling subscribers, which may read it.
    const deliveries: PendingDelivery[] = [];
    for (const [instanceId, type] of changes) {
      for (const subscription of subscriptions) {
        if (subscription.instanceId === instanceId) {
          deliveries.push({subscription, sessionId, type});
        }
      }
    }
    for (const {subscription, sessionId: queuedSession, type} of deliveries) {
      // Cleanup and resubscribe cannot revive an event queued for an old watch.
      if (subscriptions.has(subscription) && queuedSession === sessionId) {
        const {instanceId, receive} = subscription;
        if (type === 'removed') {
          unsubscribe(subscription);
        }
        deliver(
          receive,
          type === 'removed'
            ? {sessionId, type, instanceId}
            : {sessionId, type, snapshot: getSnapshot(instanceId)!},
        );
      }
    }
  }

  function isCurrentTarget(target: InspectionTarget): boolean {
    return target.sessionId === sessionId && published.has(target.instanceId);
  }

  const service: ViewInspectionService = Object.freeze({
    version: 1,
    inspect(element: Element) {
      return Object.freeze({sessionId, ...getViewsForElement(element)});
    },
    get(target: InspectionTarget) {
      return isCurrentTarget(target)
        ? getSnapshot(target.instanceId)
        : undefined;
    },
    subscribe(
      target: InspectionTarget,
      receive: (event: InspectionEvent) => void,
    ) {
      if (!isCurrentTarget(target)) {
        return undefined;
      }
      const subscription: Subscription = {
        instanceId: target.instanceId,
        receive,
      };
      subscriptions.add(subscription);
      // Close the discovery/subscribe race with the current committed snapshot.
      deliver(receive, {
        type: 'updated',
        sessionId,
        snapshot: getSnapshot(target.instanceId)!,
      });
      return () => unsubscribe(subscription);
    },
  });

  window.addEventListener('pagehide', () => {
    sessionId = `${sessionPrefix}:${++sessionGeneration}`;
    snapshots.clear();
    // Keep watch identities so a reset callback can cancel a queued peer.
    for (const subscription of Array.from(subscriptions)) {
      const {receive} = subscription;
      unsubscribe(subscription);
      deliver(receive, {sessionId, type: 'reset'});
    }
  });
  return {
    service,
    schedule(instance: InspectionInstance) {
      const scheduled = pending.size > 0;
      pending.add(instance);
      if (!scheduled) {
        Promise.resolve().then(publishPendingInspections);
      }
    },
  };
}

let state: ReturnType<typeof createService> | undefined;

/** Called only by debug refs/effects; initialize on commit, never during render. */
export function scheduleInspectionPublication(
  instance: InspectionInstance,
  readAttachedIds?: (element: Element) => readonly number[],
): void {
  if (!state && readAttachedIds && typeof window !== 'undefined') {
    state = createService(readAttachedIds);
  }
  state?.schedule(instance);
}

/** The endpoint for this engine runtime, available after its first debug commit. */
export function getViewInspectionService(): ViewInspectionService | undefined {
  return state?.service;
}
